import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { forgotPassword } from '@/lib/api';

export default function Forgot() {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const fd = new FormData(event.currentTarget);
      await forgotPassword(String(fd.get('email') ?? '').trim());
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset link');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      stamp="Forgot password"
      title="Reset access"
      subtitle="We'll email a link to set a new password if the address is on file."
      footer={
        <Link to="/auth/sign-in" className="text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <p className="text-sm" data-testid="sent">
          If an account exists for that email, a reset link is on its way. Check your inbox
          (and spam folder), then come back and sign in.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-wider">
              Email
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              className="h-11 text-base"
              data-testid="email"
            />
          </div>
          {error ? (
            <p className="text-sm text-warning font-medium" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" className="w-full h-11 text-base" disabled={pending} data-testid="submit">
            {pending ? 'Sending…' : 'Send reset link'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
